import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Custom hook for fetching branches
 * @returns {Object} - { branches, loading, error, refetch }
 */
export const useBranches = () => {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBranches = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/branches`);
      setBranches(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load branches');
      console.error('Failed to load branches:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  return { branches, loading, error, refetch: fetchBranches };
};

/**
 * Custom hook for fetching team members/users
 * @param {Object} options - { filterByBranch: branchId }
 * @returns {Object} - { teamMembers, loading, error, refetch, getFilteredByBranch }
 */
export const useTeamMembers = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTeamMembers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/users`);
      setTeamMembers(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load team members');
      console.error('Failed to load team members:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeamMembers();
  }, [fetchTeamMembers]);

  // Filter team members by branch (includes admins who work across all branches)
  const getFilteredByBranch = useCallback((branchId) => {
    if (!branchId || branchId === 'all') {
      return teamMembers;
    }
    return teamMembers.filter(member => 
      member.branch_id === branchId || member.role === 'admin'
    );
  }, [teamMembers]);

  return { teamMembers, loading, error, refetch: fetchTeamMembers, getFilteredByBranch };
};

/**
 * Custom hook for fetching customers
 * @returns {Object} - { customers, loading, error, refetch, searchCustomers }
 */
export const useCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load customers');
      console.error('Failed to load customers:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  // Search customers by name or phone (minimum 3 characters)
  const searchCustomers = useCallback((query) => {
    if (!query || query.length < 3) {
      return [];
    }
    const lowerQuery = query.toLowerCase();
    return customers.filter(c => 
      c.name.toLowerCase().includes(lowerQuery) ||
      c.phone?.toLowerCase().includes(lowerQuery)
    );
  }, [customers]);

  return { customers, loading, error, refetch: fetchCustomers, searchCustomers };
};

/**
 * Custom hook for fetching enquiries
 * @returns {Object} - { enquiries, loading, error, refetch }
 */
export const useEnquiries = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchEnquiries = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/enquiries`);
      setEnquiries(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load enquiries');
      console.error('Failed to load enquiries:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEnquiries();
  }, [fetchEnquiries]);

  return { enquiries, loading, error, refetch: fetchEnquiries };
};

/**
 * Custom hook for fetching tickets
 * @returns {Object} - { tickets, loading, error, refetch }
 */
export const useTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/tickets`);
      setTickets(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load tickets');
      console.error('Failed to load tickets:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  return { tickets, loading, error, refetch: fetchTickets };
};

/**
 * Custom hook for fetching dashboard stats
 * @returns {Object} - { stats, loading, error, refetch }
 */
export const useDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load dashboard');
      console.error('Failed to load dashboard:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, loading, error, refetch: fetchStats };
};

/**
 * Custom hook for fetching marketing leads
 * @returns {Object} - { leads, loading, error, refetch }
 */
export const useMarketingLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/marketing-leads`);
      setLeads(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load marketing leads');
      console.error('Failed to load marketing leads:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  return { leads, loading, error, refetch: fetchLeads };
};

/**
 * Custom hook for fetching marketing report stats
 * @returns {Object} - { stats, loading, error, refetch }
 */
export const useMarketingReports = (startDate = null, endDate = null) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/marketing-reports/stats`;
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url);
      setStats(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load marketing reports');
      console.error('Failed to load marketing reports:', err);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, loading, error, refetch: fetchStats };
};
